/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import type { DestinationMap } from '../types';
import type { BannerApiResponse } from '../types/banner';

const SNAP_DESTINATIONS_ENDPOINT = '/flights/v3/destination';
const SNAP_LOCATION_FILTER_ENDPOINT = '/snap/location';

type Environment = 'stg' | 'prd';
type LogErrorFn = (error: unknown, context?: Record<string, any>) => void;

export type SnapLocationFilterItem = {
  id: string;
  text?: string;
  name?: string;
  value?: string;
  hotels_count?: number;
};

export type SnapLocationFilterResponse = {
  location?: Array<{
    data?: SnapLocationFilterItem[];
  }>;
};

export class SnapStaticApiService {
  private readonly baseClient: ApiClient;
  private readonly environment: Environment;
  private readonly logError?: LogErrorFn;

  constructor(environment: Environment = 'stg', logError?: LogErrorFn) {
    this.environment = environment;
    this.logError = logError;
    this.baseClient = CoreNetworking({
      baseUrl: this.getHost(environment),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, */*',
      },
    });
  }

  private getHost(environment: Environment): string {
    return environment === 'stg'
      ? 'https://static.stgairasia.com'
      : 'https://static.airasia.com';
  }

  protected async get<T>(
    url: string,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.baseClient.get(url, {
        params,
        headers,
      });
      return response.data;
    } catch (error) {
      this.handleError(error, { url, method: 'GET' });
      throw error;
    }
  }

  private handleError(error: unknown, context?: Record<string, any>): void {
    if (this.logError) {
      this.logError(error, context);
    } else {
      console.error('SnapStaticApiService Error:', error, context);
    }
  }

  /**
   * Fetches valid destinations for a given station code
   * @param stationCode - Station code (e.g., 'PEN', 'KUL')
   * @returns Map of destinations grouped by index
   */
  public async getDestinations(stationCode: string): Promise<DestinationMap> {
    return this.get<DestinationMap>(
      `${SNAP_DESTINATIONS_ENDPOINT}/${stationCode.toLowerCase()}/file.json`,
    );
  }

  /**
   * Fetches legacy SNAP location filter options.
   * Existing SNAP renders the Landmark/Location filter from this metadata file,
   * not from hotel smartfill nearby_poi.
   */
  public async getLocationFilters(
    location: string,
    language: string,
  ): Promise<SnapLocationFilterResponse> {
    return this.get<SnapLocationFilterResponse>(
      `${SNAP_LOCATION_FILTER_ENDPOINT}/${location.toLowerCase()}/${language}/file.json`,
      {
        ignoreCache: 1,
      },
    );
  }

  /**
   * Fetch SNAP banners from static bucket
   */
  public async fetchSnapBanners(): Promise<BannerApiResponse> {
    return this.get<BannerApiResponse>(
      `/hotels/country-banner/snap-search/${this.environment}/file.json`,
      undefined,
      {
        'Cache-Control': 'no-store, no-cache, must-revalidate',
        Pragma: 'no-cache',
      },
    );
  }
}
